import { Component, OnDestroy, ViewChild, Output, EventEmitter } from '@angular/core'
import { FormBuilder, FormGroup, AbstractControl } from '@angular/forms'
import { Subscription } from 'rxjs'
import { Table } from 'primeng/table'
import { AccountSubjectGroupDto, IdentityProviderDto } from 'src/app/services/dtos'
import { UiService } from 'src/app/services/ui.service'
import { subscriptionActive } from 'src/utils/rxjs-utils'
import { AccountService } from '../../services/account.service'
import { ApplicationProviderService } from '../../services/application.service'
import { ModalDialogComponent } from 'src/app/shared/modal-dialog/modal-dialog.component'
import { ApiService } from 'src/app/services/api.service'
import { ModalService, ModalController } from '../../services/modal.service'
import { AccessLevelService } from 'src/app/services/access-level.service'
import { RoleLoadingService, RoleItem, AccessLevelInfo } from 'src/app/services/role-loading.service'

export interface GroupItem {
  id: string
  name: string
  roleCount: number
  selected: boolean
}

// Custom validator for email list
function emailListValidator(control: AbstractControl): { [key: string]: any } | null {
  if (!control.value) return { required: true }

  const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  const emails = control.value
    .trim()
    .split(/\s+/)
    .filter((email: string) => email.length > 0)

  if (emails.length === 0) {
    return { required: true }
  }

  const invalidEmails = emails.filter((email: string) => !emailPattern.test(email))
  if (invalidEmails.length > 0) {
    return { invalidEmails: invalidEmails }
  }

  return null
}

@Component({
  selector: 'app-invite-users-modal',
  templateUrl: './invite-users-modal.component.html',
  standalone: false,
})
export class InviteUsersModalComponent implements OnDestroy, ModalController {
  @ViewChild('inviteUsersModal') modal: ModalDialogComponent
  @ViewChild('rolesTable') rolesTable: Table
  @ViewChild('groupsTable') groupsTable: Table
  @Output() usersInvited = new EventEmitter<void>()
  loadingRoles = false
  sendingInvites = false
  sendInvitesSubscription: Subscription = null

  inviteForm: FormGroup
  emailTags: string[] = []
  currentEmailInput = ''
  emailInputTouched = false

  // PrimeNG table selections
  selectedRoles: RoleItem[] = []
  selectedGroups: GroupItem[] = []

  // Available roles loaded from API
  availableRoles: RoleItem[] = []

  // Available groups loaded from API
  availableGroups: GroupItem[] = []

  // Cache to avoid unnecessary API calls
  private groupsCache: GroupItem[] | null = null

  constructor(
    private formBuilder: FormBuilder,
    public accountService: AccountService,
    private applicationProviderService: ApplicationProviderService,
    private uiService: UiService,
    private apiService: ApiService,
    private modalService: ModalService,
    public accessLevelService: AccessLevelService,
    private roleLoadingService: RoleLoadingService
  ) {
    this.initializeForm()
    // Register with modal service
    this.modalService.register(this, this)
  }

  /**
   * Returns the list of identity providers available for SSO user management.
   * Filters out MS Entra IdPs that are used by applications for Azure environment access.
   */
  get ssoIdentityProviders(): IdentityProviderDto[] {
    return this.accountService.listSsoIdentityProviders(this.applicationProviderService.getUsedIdentityProviderIds())
  }

  // Helper method to identify default account roles
  public isDefaultAccountRole = (role: RoleItem): boolean => {
    return this.accessLevelService.isDefaultAccountRole(role)
  }

  ngOnDestroy() {
    if (subscriptionActive(this.sendInvitesSubscription)) {
      this.sendInvitesSubscription.unsubscribe()
    }
    // Unregister from modal service
    this.modalService.unregister(this)
  }

  // Method to clear cache when roles/groups are updated elsewhere
  clearCache() {
    this.roleLoadingService.clearCache()
    this.groupsCache = null
  }

  private initializeForm() {
    this.inviteForm = this.formBuilder.group({
      emailList: ['', [emailListValidator]],
      identityProvider: [''],
    })
  }

  open(): Promise<void> {
    this.resetForm()
    this.loadingRoles = true

    return Promise.all([this.loadAvailableRoles(), this.loadAvailableGroups()]).then(() => {
      this.loadingRoles = false

      // Set default identity provider - default to Arpio if it's the only option
      const idps = this.ssoIdentityProviders
      let defaultIdp = ''
      if (idps.length === 1) {
        defaultIdp = idps[0].identityProviderId
      }
      this.inviteForm.patchValue({ identityProvider: defaultIdp })

      this.modal.open()
    })
  }

  close() {
    this.modal.close()
    this.resetForm()
  }

  isModalOpen(): boolean {
    return this.modal && this.modal.isOpen
  }

  private resetForm() {
    this.inviteForm.reset()
    this.emailTags = []
    this.currentEmailInput = ''
    this.emailInputTouched = false
    this.resetSelections()
    this.sendingInvites = false
    if (this.sendInvitesSubscription) {
      this.sendInvitesSubscription.unsubscribe()
      this.sendInvitesSubscription = null
    }
  }

  private resetSelections() {
    this.selectedRoles = []
    this.selectedGroups = []
    this.availableRoles.forEach((role) => (role.selected = false))
    this.availableGroups.forEach((group) => (group.selected = false))
  }

  private async loadAvailableRoles(): Promise<void> {
    const allRoles = await this.roleLoadingService.loadRoles(true) // Use cache

    // Filter to only system roles if rbacFull is not enabled
    if (!this.accountService.rbacFullEnabled) {
      this.availableRoles = allRoles.filter((role) => role.isSystemRole)
    } else {
      this.availableRoles = allRoles
    }
  }

  private loadAvailableGroups(): Promise<void> {
    // Use cached data if available
    if (this.groupsCache) {
      this.availableGroups = this.groupsCache.map((group) => ({ ...group, selected: false }))
      return Promise.resolve()
    }

    return new Promise((resolve) => {
      this.apiService.listAccountSubjectGroups(this.accountService.accountId).subscribe(
        (groupDtos) => {
          const groups = groupDtos.map((groupDto) => ({
            id: groupDto.accountSubjectGroupId || '',
            name: groupDto.name || '',
            roleCount: groupDto.roles?.length || 0,
            selected: false,
          }))

          // Cache the groups for future use
          this.groupsCache = [...groups]
          this.availableGroups = groups

          resolve()
        },
        (error) => {
          console.error('Error loading groups:', error)
          this.availableGroups = []
          resolve()
        }
      )
    })
  }

  onEmailInputChange(event: any) {
    this.currentEmailInput = event.target.value
    this.emailInputTouched = true
  }

  onEmailInputKeydown(event: KeyboardEvent) {
    if (event.key === ' ' || event.key === 'Enter') {
      event.preventDefault()
      this.addEmailTag()
    } else if (event.key === 'Backspace' && this.currentEmailInput === '' && this.emailTags.length > 0) {
      // Remove last tag when backspacing on empty input
      this.removeEmailTag(this.emailTags.length - 1)
    }
  }

  onEmailInputBlur() {
    if (this.currentEmailInput.trim()) {
      this.addEmailTag()
    }
  }

  addEmailTagFromButton() {
    this.addEmailTag()
    this.emailInputTouched = true
  }

  private addEmailTag() {
    const email = this.currentEmailInput.trim()
    if (email && !this.emailTags.includes(email)) {
      this.emailTags.push(email)
      this.currentEmailInput = ''
      this.updateFormValue()
    }
  }

  removeEmailTag(index: number) {
    this.emailTags.splice(index, 1)
    this.updateFormValue()
  }

  private updateFormValue() {
    const newValue = this.emailTags.join(' ')
    this.inviteForm.patchValue({ emailList: newValue })
  }

  isEmailInvalid(email: string): boolean {
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return !emailPattern.test(email)
  }

  // Form validation
  get isFormValid() {
    const hasValidEmails = this.inviteForm.get('emailList')?.valid

    return hasValidEmails
  }

  get emailListErrors() {
    const control = this.inviteForm.get('emailList')
    if (control?.errors) {
      if (control.errors['required']) {
        return 'At least one email address is required'
      }
      if (control.errors['invalidEmails']) {
        return `Invalid email addresses: ${control.errors['invalidEmails'].join(', ')}`
      }
    }
    return null
  }

  get rbacEnabled(): boolean {
    return this.accountService.rbacEnabled
  }

  async onSendInvites() {
    if (!this.isFormValid) return

    this.sendingInvites = true

    const identityProvider =
      this.inviteForm.get('identityProvider')?.value || this.ssoIdentityProviders[0]?.identityProviderId
    const selectedRoleIds = this.selectedRoles.map((role) => role.id)

    try {
      // Create users with direct role assignments
      const createdUsers = await Promise.all(
        this.emailTags.map((email) =>
          this.apiService
            .createAccountSubject(
              this.accountService.accountId,
              email,
              identityProvider,
              selectedRoleIds,
              email // Use email as name initially
            )
            .toPromise()
        )
      )

      // Add users to selected groups
      if (this.selectedGroups.length > 0) {
        await this.addUsersToGroups(createdUsers)
      }

      this.sendingInvites = false
      this.close()
      this.usersInvited.emit() // Notify parent component to refresh user data
    } catch (error) {
      this.sendingInvites = false
      console.error('Error sending invites:', error)

      // Use enhanced UiService for user-friendly error display
      this.uiService.announceApiError(error, 'Failed to send user invitations')
    }
  }

  private async addUsersToGroups(createdUsers: any[]): Promise<void> {
    const groupUpdatePromises = this.selectedGroups.map(async (selectedGroup) => {
      try {
        // Get full group data from API
        const fullGroup: AccountSubjectGroupDto = await this.apiService
          .getAccountSubjectGroup(this.accountService.accountId, selectedGroup.id)
          .toPromise()

        // Add new user IDs to the group's accountSubjects array
        const newUserIds = createdUsers.map((user) => user.accountSubjectId)
        const updatedAccountSubjects = [...(fullGroup.accountSubjects || []), ...newUserIds]

        // Prepare update payload with only updatable fields
        // roles is already a string[] according to AccountSubjectGroupDto interface
        const updatePayload: any = {
          name: fullGroup.name,
          externalId: fullGroup.externalId,
          accountSubjects: updatedAccountSubjects,
        }

        // Only include roles if they exist and are not empty
        if (fullGroup.roles && fullGroup.roles.length > 0) {
          updatePayload.roles = fullGroup.roles
        }

        return this.apiService
          .updateAccountSubjectGroup(this.accountService.accountId, selectedGroup.id, updatePayload)
          .toPromise()
      } catch (error) {
        console.error(`Failed to add users to group ${selectedGroup.name}:`, error)
        throw error
      }
    })

    await Promise.all(groupUpdatePromises)
  }

  roleTrackBy(_index: number, role: RoleItem) {
    return role.id
  }

  groupTrackBy(_index: number, group: GroupItem) {
    return group.id
  }

  // Custom checkbox methods for roles
  areAllRolesSelected(): boolean {
    return this.selectedRoles.length > 0 && this.selectedRoles.length === this.availableRoles.length
  }

  areSomeRolesSelected(): boolean {
    return this.selectedRoles.length > 0 && this.selectedRoles.length < this.availableRoles.length
  }

  isRoleSelected(role: RoleItem): boolean {
    return this.selectedRoles.some((r) => r.id === role.id)
  }

  toggleAllRoles(event: Event): void {
    const target = event.target as HTMLInputElement
    const isChecked = target.checked

    if (isChecked) {
      this.selectedRoles = [...this.availableRoles]
    } else {
      this.selectedRoles = []
    }
  }

  toggleRole(role: RoleItem, event: Event): void {
    const target = event.target as HTMLInputElement
    const isChecked = target.checked

    if (isChecked) {
      if (!this.isRoleSelected(role)) {
        this.selectedRoles = [...this.selectedRoles, role]
      }
    } else {
      this.selectedRoles = this.selectedRoles.filter((r) => r.id !== role.id)
    }
  }

  // Custom checkbox methods for groups
  areAllGroupsSelected(): boolean {
    return this.selectedGroups.length > 0 && this.selectedGroups.length === this.availableGroups.length
  }

  areSomeGroupsSelected(): boolean {
    return this.selectedGroups.length > 0 && this.selectedGroups.length < this.availableGroups.length
  }

  isGroupSelected(group: GroupItem): boolean {
    return this.selectedGroups.some((g) => g.id === group.id)
  }

  toggleAllGroups(event: Event): void {
    const target = event.target as HTMLInputElement
    const isChecked = target.checked

    if (isChecked) {
      this.selectedGroups = [...this.availableGroups]
    } else {
      this.selectedGroups = []
    }
  }

  toggleGroup(group: GroupItem, event: Event): void {
    const target = event.target as HTMLInputElement
    const isChecked = target.checked

    if (isChecked) {
      if (!this.isGroupSelected(group)) {
        this.selectedGroups = [...this.selectedGroups, group]
      }
    } else {
      this.selectedGroups = this.selectedGroups.filter((g) => g.id !== group.id)
    }
  }

  customSort = this.accessLevelService.createAccessLevelSortFunction()

  filterRoles(event: any) {
    if (this.rolesTable) {
      this.rolesTable.filterGlobal(event.target.value, 'contains')
    }
  }

  filterGroups(event: any) {
    if (this.groupsTable) {
      this.groupsTable.filterGlobal(event.target.value, 'contains')
    }
  }

  // Generate tooltip content for access level badges
  getAccessLevelTooltip(accessLevelInfo: AccessLevelInfo): string {
    if (!accessLevelInfo.applications || accessLevelInfo.applications.length === 0) {
      return 'All Applications'
    }

    const badgeHtml = accessLevelInfo.applications.map((name) => `<span class="tooltip-badge">${name}</span>`).join('')
    return `<div class="tooltip-badges-container">${badgeHtml}</div>`
  }
}
